import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import {
  Tablist,
  LoadingIndicator,
  Heading
} from "@lululemon/dsp-ecom-pattern-library";
import { appEnv } from "@lululemon/ecom-core";
import {
  useDeviceOrientation,
  DEVICES
} from "@lululemon/dsp-legacy-shared/src/utils/device-orientation";
import { ErrorBoundary, sentryLog } from "@lululemon/dsp-shared";
import { LocationPicker, Map, List } from "../store-locator";
import { storeAmenitiesPropType } from "../../util/amenities";

import styles from "./store-locator-wrapper.module.scss";

import content from "../../content/content";
import {
  calcDistance,
  createGoogleLatLng,
  googleMapsClient,
  MAX_DISTANCE,
  NA_INITIAL_LAT_LNG,
  useGoogleMapLoader,
  sortByKey
} from "@lululemon/dsp-utils";

const slContent = content.components.storeLocator;

const MOBILE_VIEWS = {
  list: "list",
  map: "map"
};

const MOBILE_VIEW_WIDTH = 768;

function useWindowWidth() {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const updateWindowDimensions = () => {
      setWidth(window.innerWidth);
    };
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);

    return () => window.removeEventListener("resize", updateWindowDimensions);
  }, []);

  return width;
}

export const StoreLocatorWrapper = ({
  storeListData = [],
  publicConfig,
  showAllStoresBtn = false,
  className = "",
  selectThisStore = false,
  onStoreSelected,
  isModalView
}) => {
  const [mobileView, setMobileView] = useState(MOBILE_VIEWS.list);
  const [infoBoxIsOpen, setInfoBoxIsOpen] = useState(false);
  const [position, setPosition] = useState(NA_INITIAL_LAT_LNG);
  const [hasSearchedForPlace, setHasSearchedForPlace] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const isGoogleMapLoaded = useGoogleMapLoader();

  const [device] = useDeviceOrientation();

  const width = useWindowWidth();
  const isMobileView = width < MOBILE_VIEW_WIDTH;

  const nearestStores = useMemo(() => {
    if (!storeListData) {
      return [];
    }

    if (!isGoogleMapLoaded || !googleMapsClient()) {
      return [];
    }

    if (!position) {
      return [];
    }

    const filteredStoreList = storeListData
      .filter((store) =>
        Object.keys(selectedFilters).every(
          (filter) => store.storeLocatorAmenities[filter]
        )
      )
      .filter(
        (store) =>
          typeof store.latitude === "number" &&
          typeof store.longitude === "number"
      );

    const positioned = filteredStoreList.map((store) => ({
      ...store,
      position: createGoogleLatLng(store)
    }));

    const computed = positioned.map((store) => ({
      ...store,
      distance: calcDistance(position, store.position)
    }));

    const filtered = computed.filter((store) => store.distance <= MAX_DISTANCE);
    return sortByKey(filtered, "distance");
  }, [storeListData, isGoogleMapLoaded, position, selectedFilters]);

  const updatePosition = useCallback(
    (place) => {
      setHasSearchedForPlace(true);

      try {
        const pos = createGoogleLatLng(place);
        setPosition({
          lat: pos?.lat(),
          lng: pos?.lng()
        });
      } catch (err) {} // eslint-disable-line
    },
    [setHasSearchedForPlace, setPosition]
  );
  const updateFilters = useCallback(
    (filters) => {
      const mapFilters = filters.length
        ? Object.assign(...filters.map((k) => ({ [k]: true })))
        : {};
      setSelectedFilters(mapFilters);
    },
    [setSelectedFilters]
  );
  const refreshPosition = useCallback(() => {
    if (!navigator) return;
    navigator.geolocation.getCurrentPosition(updatePosition);
  }, [updatePosition]);

  // attempt geoLocate once only when google maps has loaded
  useEffect(() => {
    if (!isGoogleMapLoaded) return;
    if (initialized && mobileView !== MOBILE_VIEWS.map) return;

    setInitialized(true);
    refreshPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapLoaded, initialized, mobileView]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <section className={styles.storesSearchResultsContainer}>
        <div className={styles.storesSearchResultsContainerHeading}>
          <Heading className={cs("lll-text-large", "OneLinkTx")} tag="h1">
            {slContent.heading}
          </Heading>
          {device === DEVICES.mobile && (
            <Tablist
              className={styles.TablistWrapper}
              selectedIndex={mobileView === MOBILE_VIEWS.list ? 0 : 1}
            >
              <button
                onClick={() => {
                  setMobileView(MOBILE_VIEWS.list);
                }}
                type="button"
                className={styles.tabsBtn}
              >
                {slContent.tabList.list}
              </button>
              <button
                onClick={() => {
                  setMobileView(MOBILE_VIEWS.map);
                }}
                type="button"
                className={styles.tabsBtn}
              >
                {slContent.tabList.map}
              </button>
            </Tablist>
          )}
        </div>
        {isGoogleMapLoaded && (
          <LocationPicker
            handleOnPlaceChanged={updatePosition}
            handleOnApplyFilters={updateFilters}
            hasSearchedForPlace={hasSearchedForPlace}
            storeListLength={nearestStores?.length}
            isMobileView={isMobileView}
            isModalView={isModalView}
          />
        )}
        {isGoogleMapLoaded ? (
          <List
            className={
              device === DEVICES.mobile && mobileView !== MOBILE_VIEWS.list
                ? styles.hiddenTabContent
                : className
            }
            storeList={nearestStores}
            publicConfig={publicConfig}
            showAllStoresBtn={showAllStoresBtn}
            selectThisStore={selectThisStore}
            onStoreSelected={onStoreSelected}
          />
        ) : (
          <LoadingIndicator />
        )}
      </section>
      <section
        className={cs(
          styles.storesSearchMapContainer,
          isMobileView && mobileView !== MOBILE_VIEWS.map
            ? styles.hiddenTabContent
            : ""
        )}
      >
        {isGoogleMapLoaded ? (
          <Map
            googleMapsScriptIsLoaded={isGoogleMapLoaded}
            isMobile={device === "mobile"}
            storeList={nearestStores}
            selectedPosition={position}
            handleOnLocateClick={refreshPosition}
            infoBoxIsOpen={infoBoxIsOpen}
            setInfoBoxIsOpen={setInfoBoxIsOpen}
            publicConfig={publicConfig}
            selectThisStore={selectThisStore}
            onStoreSelected={onStoreSelected}
          />
        ) : (
          <LoadingIndicator />
        )}
      </section>
    </>
  );
};

StoreLocatorWrapper.propTypes = {
  storeListData: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      fullAddress: PropTypes.string,
      hours: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          openHour: PropTypes.string,
          closeHour: PropTypes.string
        })
      ),
      id: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      message: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
      specialHours: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          description: PropTypes.string,
          openHour: PropTypes.string,
          closeHour: PropTypes.string
        })
      ),
      state: PropTypes.string,
      storeType: PropTypes.string,
      storeLocatorAmenities: storeAmenitiesPropType
    })
  ),
  selectThisStore: PropTypes.bool,
  showAllStoresBtn: PropTypes.bool,
  className: PropTypes.string,
  onStoreSelected: PropTypes.func,
  publicConfig: PropTypes.shape({
    googleMapsSearchDomain: PropTypes.string,
    googleMapsApiKey: PropTypes.string,
    nav: PropTypes.shape({
      apiHost: PropTypes.string,
      apiHostBasePath: PropTypes.string
    })
  }),
  isModalView: PropTypes.bool
};

StoreLocatorWrapper.defaultProps = {
  storeListData: [],
  selectThisStore: false,
  showAllStoresBtn: false,
  className: "",
  onStoreSelected: () => {},
  publicConfig: {},
  isModalView: false
};

const handleError = (error, errorInfo) => {
  if (appEnv.isDev()) {
    console.error("store-locator-wrapper error", error, errorInfo);
  }
  sentryLog.error(error, {
    context: error,
    tags: {
      componentName: "store-locator-wrapper",
      functionName: "StoreLocatorWrapper"
    }
  });
};

function withErrorBoundary(TheComponent, errorBoundaryProps) {
  // eslint-disable-next-line react/display-name
  return (props) => {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <TheComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default withErrorBoundary(StoreLocatorWrapper, {
  fallback: () => null,
  onError: handleError
});
